import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CurrencyOption, CURRENCIES, DEFAULT_CURRENCY } from '../types';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { marketDecimals } from '../utils/formatters';
import { CLOUD_DATA_HYDRATED_EVENT } from '../lib/cloud/reconcile';

interface CurrencyContextValue {
  currency: CurrencyOption;
  setCurrencyCode: (code: string) => void;
  rate: number;
  /** Full USD-based rate map (`rates[X]` = units of X per 1 USD). Exposed so
   *  entry forms can capture the USD *and* AUD legs of a transaction without
   *  spinning up a second useExchangeRates instance. */
  rates: Record<string, number>;
  ratesLoading: boolean;
  ratesError: string | null;
  /** Format a USD value in the selected currency */
  fmt: (usdValue: number, compact?: boolean) => string;
  /**
   * Format a value that is ALREADY in the selected currency.
   *
   * Cost-like figures are converted per transaction at each transaction's own
   * date (see utils/displayRates), so by the time they reach a component they are
   * already denominated correctly. Passing one through `fmt` would convert it a
   * second time, at today's rate — which is the bug this pair of functions exists
   * to keep apart.
   */
  fmtDisplay: (value: number, compact?: boolean) => string;
}

/**
 * Fired when the display currency changes.
 *
 * `usePortfolio` lives ABOVE CurrencyProvider in the tree (App.tsx builds the
 * portfolio before the providers wrap it), so it cannot read this context — but it
 * now needs the display currency to convert cost figures at each transaction's own
 * date. This event is how it finds out, mirroring CLOUD_DATA_HYDRATED_EVENT, which
 * exists for exactly the same above-the-provider problem.
 */
export const CURRENCY_CHANGED_EVENT = 'mycryptoplanner:currency-changed';

/** The display currency, readable without the context. */
export function loadCurrencyCode(): string {
  try {
    return localStorage.getItem('mycryptoplanner_currency') ?? DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currencyCode, setCurrencyCode] = useState<string>(() => {
    return localStorage.getItem('mycryptoplanner_currency') ?? DEFAULT_CURRENCY;
  });

  const { rates, loading: ratesLoading, error: ratesError } = useExchangeRates();

  // Currency is read synchronously from localStorage on mount. After the login
  // reconcile hydrates cloud preferences, re-read so the cloud value takes effect.
  useEffect(() => {
    const reread = () => {
      setCurrencyCode(localStorage.getItem('mycryptoplanner_currency') ?? DEFAULT_CURRENCY);
      // Same reason as in handleSetCurrency: the hydrate can change the currency.
      window.dispatchEvent(new Event(CURRENCY_CHANGED_EVENT));
    };
    window.addEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
    return () => window.removeEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
  }, []);

  // An unrecognised stored code (hand-edited, or a currency we dropped) falls back
  // to the default rather than to whatever happens to be first in the list.
  const currency =
    CURRENCIES.find((c) => c.code === currencyCode) ??
    CURRENCIES.find((c) => c.code === DEFAULT_CURRENCY) ??
    CURRENCIES[0];
  const rate = rates[currencyCode] ?? 1;

  const handleSetCurrency = useCallback((code: string) => {
    setCurrencyCode(code);
    localStorage.setItem('mycryptoplanner_currency', code);
    // Tell the parts of the tree that sit above this provider (see
    // CURRENCY_CHANGED_EVENT) so they can re-convert at the new currency.
    window.dispatchEvent(new Event(CURRENCY_CHANGED_EVENT));
  }, []);

  /**
   * Format a value that is already in `currencyCode`. The only difference between
   * `fmt` and `fmtDisplay` is whether the caller's number still needs converting;
   * every formatting decision below is shared, so the two can never present the
   * same amount differently.
   */
  const fmtDisplay = useCallback(
    (value: number, compact = false): string => {
      const isJPY = currencyCode === 'JPY';
      // Magnitude-adaptive decimals so small coins keep precision (ADA $0.3100)
      // while larger ones stay at 2 (BTC $61,500.00). JPY shows no decimals.
      const fractions = isJPY ? 0 : marketDecimals(Math.abs(value));

      if (compact && Math.abs(value) >= 1_000_000) {
        const sym = getSymbol(currencyCode);
        const sign = value < 0 ? '-' : '';
        return `${sign}${sym}${(Math.abs(value) / 1_000_000).toFixed(2)}M`;
      }

      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: fractions,
          maximumFractionDigits: fractions,
        }).format(value);
      } catch {
        return `${getSymbol(currencyCode)}${value.toFixed(fractions)}`;
      }
    },
    [currencyCode]
  );

  /** Format a USD value, converting at TODAY's rate. Correct for what something
   *  is worth now; NOT for what it cost — see `fmtDisplay`. */
  const fmt = useCallback(
    (usdValue: number, compact = false): string => fmtDisplay(usdValue * rate, compact),
    [fmtDisplay, rate]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrencyCode: handleSetCurrency, rate, rates, ratesLoading, ratesError, fmt, fmtDisplay }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
}

function getSymbol(code: string): string {
  try {
    return (0).toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .replace(/\d/g, '').trim();
  } catch {
    return code + ' ';
  }
}
